import { addDoc, setDoc } from "firebase/firestore";
import { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useCollection } from "react-firebase-hooks/firestore";
import {
  cSuiteCollectionRef,
  checkIfUserExistsAndJoined,
  checkUserExist,
  getCSuitDoc,
  getUserDoc,
} from "../utils/firestoreHelpers";
import { designationQuery } from "../utils/queryHelpers";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const initialError = {
  firstName: "",
  lastName: "",
  email: "",
  designation: "",
  company: "",
  mobileNumber: "",
  linkedInLink: "",
  promoCode: "",
  androidRedeemCode: "",
};

const AddCSuiteUsersModal = ({ onClose }) => {
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    designation: "",
    company: "",
    mobileNumber: "",
    linkedInLink: "",
    countryCode: "sg",
    dialCode: "65",
    promoCode: "",
    androidRedeemCode: "",
  });
  const [error, setError] = useState({ ...initialError });
  const [loading, setLoading] = useState(false);

  const [designationSnap, designationLoading] = useCollection(
    designationQuery(),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  const designationOptions = useMemo(() => {
    const docs = designationSnap?.docs || [];
    return docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((d) => typeof d?.title === "string" && d.title.trim())
      .map((d) => d.title.trim());
  }, [designationSnap]);

  const onChange = (e) => {
    let value = e.target.value;
    const name = e.target.name;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = async () => {
    let isValid = true;
    let tempError = { ...initialError };
    if (!data.firstName) {
      isValid = false;
      tempError["firstName"] = "First Name is required";
    }
    if (!data.lastName) {
      isValid = false;
      tempError["lastName"] = "Last Name is required";
    }
    if (!data.designation) {
      isValid = false;
      tempError["designation"] = "Designation is required";
    }
    if (!data.company) {
      isValid = false;
      tempError["company"] = "Company is required";
    }
    if (!data.email) {
      isValid = false;
      tempError["email"] = "Email is required";
    } else if (!isValidEmail(data.email)) {
      isValid = false;
      tempError["email"] = "Please enter a valid email address";
    }  else {
      let doesExist = await checkIfUserExistsAndJoined(data.email);
      if (doesExist.error) {
        isValid = false;
        tempError["email"] = doesExist.msg;
      }
    }
    if (!data.mobileNumber) {
      isValid = false;
      tempError["mobileNumber"] = "Mobile Number is required";
    }

    setError(tempError);
    return isValid;
  };

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validate();
    if (!isValid) return;
    try {
      setLoading(true);
      const payload = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        designation: data.designation,
        company: data.company,
        linkedIn: data.linkedInLink,
        dial_code: "+" + data.dialCode,
        country_code: data.countryCode.toUpperCase(),
        mobile_number: data.mobileNumber.slice(data.dialCode.length),
        status: "Created",
        id: data.email,
        promo_code: data.promoCode,
        android_redeem_code: data.androidRedeemCode,
        membership_tier:'C-Suite'
      };

      // add cSuite user
      // await addDoc(cSuiteCollectionRef, payload); // with addDoc method, user is not getting properly created in c-suite-users collection
      // create and create csuite user with id as email
      await setDoc(getCSuitDoc(data.email), payload);
      onClose();
    } catch (e) {
      setError(e.toString());
    } finally {
      setLoading(false);
    }
  };

  return (
    <form>
      <Modal show={true} onHide={() => {}}>
        <Modal.Header>
          <Modal.Title>Add C-Suite User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div class="d-flex gap-2">
            <div className="flex-fill">
              <label htmlFor="title" className="form-label d-block">
                First Name *
              </label>
              <input
                value={data.firstName}
                onChange={onChange}
                className="w-100 form-control "
                name="firstName"
                required
              />
              <p className="text-danger small">{error.firstName}</p>
            </div>
            <div className="flex-fill">
              <label htmlFor="title" className="form-label d-block">
                Last Name *
              </label>
              <input
                value={data.lastName}
                onChange={onChange}
                className="w-100 form-control"
                name="lastName"
                required
              />
              <p className="text-danger small">{error.lastName}</p>
            </div>
          </div>
          <div class="d-flex gap-2">
            <div className="flex-fill">
              <label htmlFor="title" className="form-label d-block">
                Designation *
              </label>
              <select
                value={data.designation}
                onChange={onChange}
                className="w-100 form-control"
                name="designation"
                required
                disabled={designationLoading}
              >
                <option value="">
                  {designationLoading
                    ? "Loading designations..."
                    : "Select designation"}
                </option>
                {designationOptions.map((title) => (
                  <option key={title} value={title}>
                    {title}
                  </option>
                ))}
              </select>
              <p className="text-danger small">{error.designation}</p>
            </div>
            <div className="flex-fill">
              <label htmlFor="title" className="form-label d-block">
                Company *
              </label>
              <input
                value={data.company}
                onChange={onChange}
                className="w-100 form-control"
                name="company"
                required
              />
              <p className="text-danger small">{error.company}</p>
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="title" className="form-label d-block">
              Email *
            </label>
            <input
              type="email"
              value={data.email}
              onChange={onChange}
              className="w-100 form-control"
              name="email"
              required
            />
            <p className="text-danger small">{error.email}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="title" className="form-label d-block">
              Mobile Number *
            </label>
            {/* <input
              value={data.mobileNumber}
              onChange={onChange}
              className="w-100 form-control"
              name="mobileNumber"
              required
            /> */}

            <PhoneInput
              country={"sg"}
              value={data.mobileNumber}
              onChange={(phone, phoneData) => {
                // console.log({ phone, phoneData });
                setData((prev) => ({
                  ...prev,
                  dialCode: phoneData.dialCode,
                  countryCode: phoneData.countryCode,
                  mobileNumber: phone,
                }));
              }}
            />
            <p className="text-danger small">{error.mobileNumber}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="title" className="form-label d-block">
              LinkedIn Link
            </label>
            <input
              value={data.linkedInLink}
              onChange={onChange}
              className="w-100 form-control"
              name="linkedInLink"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="title" className="form-label d-block">
              Promo Code from app store
            </label>
            <input
              value={data.promoCode}
              onChange={onChange}
              className="w-100 form-control"
              name="promoCode"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="title" className="form-label d-block">
              Promo Code from Play store
            </label>
            <input
              value={data.androidRedeemCode}
              onChange={onChange}
              className="w-100 form-control"
              name="androidRedeemCode"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="submit" class="btn btn-primary" onClick={onSubmit} disabled={loading}>
            {loading ? (
              <>
                <span
                  class="spinner-grow spinner-grow-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Loading...
              </>
            ) : (
              "Save"
            )}
          </button>
          <button type="button" class="btn btn-danger" onClick={onClose}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </form>
  );
};
export default AddCSuiteUsersModal;
